<template>
    <div>
        <CHeading variant="2">Galéria</CHeading>

        <CCardBox>
            <div class="grid grid-cols-3 gap-1 overflow-hidden hover:cursor-pointer" @click.stop="showGallery()">
                <img
                    v-for="(image, index) in images.slice(0, countsToShow.images)"
                    :key="`image-${index}`"
                    class="aspect-square size-full object-cover object-center"
                    :src="image"
                    @click.stop="showGallery(index)"
                />

                <div
                    v-for="(thumbnail, index) in thumbnails"
                    :key="`video-${index}`"
                    class="relative size-full"
                    @click.stop="showGallery(images.length + index)"
                >
                    <div class="absolute flex size-full items-center justify-center">
                        <div class="flex size-6 items-center justify-center rounded-full bg-white dark:bg-surface-card">
                            <CIcon name="play" size="xsmall" />
                        </div>
                    </div>

                    <img :alt="thumbnail" class="aspect-square size-full object-cover object-center" :src="thumbnail" />
                </div>

                <div
                    v-if="images.length + videos.length > MAX_IMAGES"
                    class="flex cursor-pointer items-center justify-center bg-primary-500/75 p-1"
                    @click.stop="showGallery(showMoreIdx)"
                >
                    <span class="text-center text-lg font-medium text-white">
                        +{{ images.length + videos.length - MAX_IMAGES }}
                    </span>
                </div>
            </div>
        </CCardBox>
    </div>
</template>

<script setup lang="ts">
import { useModal } from 'vue-final-modal'
import { clamp } from '#shared/utils'
import GalleryModal from './modals/GalleryModal.vue'

const MAX_IMAGES = 9

const props = defineProps<{
    images: string[]
    videos: string[]
}>()

// Modal setup
const { open: openGalleryModal, close: closeGalleryModal, patchOptions } = useModal({
    component: GalleryModal,
    attrs: {
        images: props.images,
        videos: props.videos,
        initialIdx: 0,
        onClose() {
            closeGalleryModal()
        }
    }
})

const totalLength = props.images.length + props.videos.length

const showMoreIdx = computed((): number => {
    let initialIdx = 0

    if (props.images.length > countsToShow.value.images) {
        initialIdx = countsToShow.value.images
    } else if (props.videos.length > countsToShow.value.videos) {
        initialIdx = props.images.length + countsToShow.value.videos
    }

    return clamp(0, initialIdx, totalLength - 1)
})

const countsToShow = computed((): { images: number; videos: number } => {
    const maxItems = totalLength > MAX_IMAGES ? MAX_IMAGES - 1 : MAX_IMAGES

    const maxImages = Math.max(maxItems / 2)
    const maxVideos = Math.min(maxItems / 2)

    let imagesCount = clamp(0, props.images.length, maxImages)
    let videosCount = clamp(0, props.videos.length, maxVideos)

    const imagesRemaining = props.images.length - imagesCount
    const videosRemaining = props.videos.length - videosCount

    if (imagesCount < maxImages && videosRemaining) {
        videosCount += clamp(0, videosRemaining, maxItems - (imagesCount + videosCount))
    }

    if (videosCount < maxVideos && imagesRemaining) {
        imagesCount += clamp(0, imagesRemaining, maxItems - (videosCount + imagesCount))
    }

    return { images: imagesCount, videos: videosCount }
})

const thumbnails = computed((): string[] => {
    if (!countsToShow.value.videos) return []

    const videosToShow = props.videos.slice(0, countsToShow.value.videos)

    return videosToShow.map((video) => {
        const youtubeId = video.match(/youtube\.com.*(\?v=|\/embed\/)(.{11})/)?.pop()
        return `//img.youtube.com/vi/${youtubeId}/0.jpg`
    })
})

const showGallery = (idx = 0) => {
    // Update modal attrs with new index
    patchOptions({
        attrs: {
            images: props.images,
            videos: props.videos,
            initialIdx: idx,
            onClose() {
                closeGalleryModal()
            }
        }
    })
    openGalleryModal()
}
</script>
